import { InfoIcon } from 'lucide-react';
import { FC, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { replacePathParams } from '@/common/utils/utils';
import LeftArrowIcon from '@/components/BackArrowIcon';
import ChatIcon from '@/components/ChatIcon';
import LeaveTableIcon from '@/components/LeaveTableIcon';
import ProfileConfiguration from '@/components/ProfileConfiguration/ProfileConfiguration';
import TableStakeInfo from '@/components/TableStakeInfo';
import useIsMobileView from '@/hooks/useIsMobileView';
import HeaderButton from '@/pages/table/components/HeaderButton/HeaderButton';
import SitOutButton from '@/pages/table/components/SitOutButton/SitOutButton';
import TableHeaderButton from '@/pages/table/components/TableHeaderButton/TableHeaderButton';
import ROUTES from '@/routers/Routes';
import { ReducerState } from '@/services/common/lib/reducerState';
import useGetTableHandHistory from '@/services/table/queries/useGetTableHandHistory';
import useGetTableRest from '@/services/table/queries/useGetTableRest';
import useGetUserTable from '@/services/table/queries/useGetUserTable';
import { showTableModal, tableRefetchHistory } from '@/services/table/tableActions';
import { TableModalTypes, PlayerSeatDto } from '@/services/table/tableState';

import RebuyButton from '../RebuyButton/RebuyButton';
import TableHeaderLabel from '../TableHeaderLabel/TableHeaderLabel';
import TableTabs from '../TableTab/TableTabs';
import styles from './TableHeader.module.css';

interface TableHeaderParams {
  userId: string;
  selectedTableId: string;
  showChat: boolean;
  setShowChat: (showChat: boolean) => void;
  game: string;
  stakes: string | ReactNode;
  playerSeat: PlayerSeatDto | undefined; //TODO check this field
  tableId: string;
  tableTabsInDesktopEnabled: boolean;
}

const TableHeader: FC<TableHeaderParams> = ({
  userId,
  selectedTableId,
  showChat,
  setShowChat,
  game,
  stakes,
  tableId,
  playerSeat,
  tableTabsInDesktopEnabled,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();
  const { data: handHistory, refetch } = useGetTableHandHistory(tableId ?? '');
  const player = useSelector((state: ReducerState) => state.players[tableId]);

  useEffect(() => {
    if (player && player.refetchHandHistory) {
      refetch();
      dispatch(tableRefetchHistory(tableId));
    }
  }, [player?.refetchHandHistory]);

  const onClickGoBackHandler = () => navigate(ROUTES.LOBBY);
  const { tableStateUrl, modal } = useSelector((state: ReducerState) => ({
    modal: state.tables?.[tableId]?.modal,
    tableStateUrl: state.tables?.[tableId]?.url,
  }));

  const onClickShowChat = () => setShowChat(!showChat);

  const onClickShowTableInfo = () => {
    if (isMobileView) {
      setShowChat(false);
    }
    dispatch(showTableModal(tableId, TableModalTypes.TABLE_INFO));
  };

  const onClickShowLeaveTable = () => {
    if (isMobileView) {
      setShowChat(false);
    }
    dispatch(showTableModal(tableId, TableModalTypes.LEAVE_TABLE));
  };

  const { data: userSeatsData } = useGetUserTable({
    userId,
    selectedTableId,
    tableUrl: tableStateUrl,
  });

  const { data: tableData } = useGetTableRest(selectedTableId, tableStateUrl);

  const tableInfo = tableData?.data;
  const isSitout = playerSeat?.active === false;

  const navigateTableHistoryHandler = () => {
    if (handHistory) {
      const handId = handHistory[handHistory.length - 1]?.handId || '';
      const width = window.innerWidth;
      const height = window.innerHeight;
      const url = replacePathParams(ROUTES.REPLAY, { tableId: tableId || '', handId });
      window.open(url, '_blank', `width=${width},height=${height},resizable=yes,scrollbars=yes,status=yes`);
    }
  };

  return (
    <div className="w-full text-[16px]">
      <header className="flex w-full items-center justify-between py-3">
        {isMobileView && (
          <div className={`mr-4 flex w-10 items-center justify-start rounded-lg ${styles.headerButtonBack}`}>
            <button onClick={onClickGoBackHandler} className="arrow">
              <LeftArrowIcon className="w-6 text-ph-gray-silver hover:text-ph-gray-white lg:w-10" />
            </button>
          </div>
        )}
        {!isMobileView && (
          <div className="mr-4 flex w-auto flex-col items-start justify-center font-manrope font-bold">
            <span className="mr-2 text-xs text-ph-gray-dark">Current Table:</span>
            <span className="flex items-center text-xs text-white">
              {game} ({stakes})
            </span>
          </div>
        )}
        {!userSeatsData?.data.seats && tableInfo && tableInfo.currentVariant && (
          <div className="mr-2 flex h-11 flex-col items-center justify-center gap-2.5 rounded-md border-2 border-white border-opacity-30 p-1.5">
            <div className="flex flex-col text-sm">
              <TableHeaderLabel>
                <>
                  {`${tableInfo?.currentVariant?.bettingStructure} ${tableInfo?.currentVariant?.category}`}
                  {`(${(<TableStakeInfo table={tableInfo} />)})`}
                </>
              </TableHeaderLabel>
            </div>
          </div>
        )}
        {!isMobileView && tableTabsInDesktopEnabled && userSeatsData && (
          <div className="mx-4 flex-1 overflow-x-auto">
            <TableTabs
              selectedTableId={selectedTableId}
              tableIds={userSeatsData?.data.seats?.map((seat) => seat.tableId) || []}
              userId={userId}
            />
          </div>
        )}
        <div className="z-10 flex flex-shrink-0 items-center justify-center gap-2 md:gap-4">
          <ChatIcon
            onClick={onClickShowChat}
            className={twMerge(
              'h-[1.375em] w-[1.375em] min-w-[1.375em] cursor-pointer text-ph-gray-silver hover:text-ph-gray-white',
              showChat ? '!text-ph-primary-violet hover:text-ph-primary-violet' : '',
            )}
          />
          <InfoIcon
            onClick={onClickShowTableInfo}
            className={twMerge(
              'h-6 w-6 min-w-6 cursor-pointer text-ph-gray-silver hover:text-ph-gray-white',
              modal ? '!text-ph-primary-violet hover:text-ph-primary-violet' : '',
            )}
          />
          {playerSeat && !isSitout && <SitOutButton tableId={tableId} />}
          <RebuyButton tableId={tableId} seat={playerSeat?.seat} />
          {playerSeat &&
            (isMobileView ? (
              <div>
                <LeaveTableIcon
                  className="h-6 w-6 text-ph-gray-silver hover:text-ph-gray-white"
                  onClick={onClickShowLeaveTable}
                />
              </div>
            ) : (
              <HeaderButton
                id="leave-table-button"
                onClick={onClickShowLeaveTable}
                icon={<LeaveTableIcon className="h-5 w-5" color="currentColor" />}
              />
            ))}
          <div>
            <ProfileConfiguration onSettingsOpen={() => isMobileView && setShowChat(false)} />
          </div>
        </div>
      </header>
      {isMobileView && userSeatsData && (
        <TableTabs
          selectedTableId={selectedTableId}
          tableIds={userSeatsData?.data.seats?.map((seat) => seat.tableId) || []}
          userId={userId}
        />
      )}
      {
        <TableHeaderButton disabled={!(handHistory && handHistory.length >= 1)} onClick={navigateTableHistoryHandler}>
          Hand History
        </TableHeaderButton>
      }
    </div>
  );
};

export default TableHeader;
